import { useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { countSoldierUsage, deleteSoldier } from '../../../db/fleet-store';
import {
  ANY,
  EMPTY_SOLDIER_FILTERS,
  filterSoldiers,
  soldierAvailabilityCounts,
} from '../../../core/fleet-filters';
import type { SoldierFilters } from '../../../core/fleet-filters';
import { EMPTY_ASSIGNMENT_INDEX } from '../../../core/effective-status';
import type { AssignmentIndex } from '../../../core/effective-status';
import { hasPermission } from '../../../core/permissions';
import { SOLDIER_AVAILABILITIES, SOLDIER_AVAILABILITY_LABELS } from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { SoldierCard } from './SoldierCard';
import { SoldierDetailsModal } from './SoldierDetailsModal';
import { SoldierFilterBar } from './SoldierFilterBar';
import { SoldierFormModal, SoldierStatusModal } from './SoldierFormModal';
import { SoldierQuickStatusSheet } from './SoldierQuickStatusSheet';

interface SoldiersPageProps {
  user: User;
  soldiers: readonly Soldier[];
  vehicles: readonly Vehicle[];
  /**
   * Who is out on an active task right now. The roster reads its statuses
   * through it, so a soldier riding a convoy is "במשימה" here without their
   * record having been touched — and stands back up when the task is closed.
   */
  assignments?: AssignmentIndex;
  loading: boolean;
  reload: () => Promise<void>;
  /** Evaluation moment for every timed status on the page. */
  now?: number;
}

/** Tab strip: every availability, plus the catch-all. */
const AVAILABILITY_TABS: readonly SoldierFilters['availability'][] = [
  ANY,
  ...SOLDIER_AVAILABILITIES,
];

type Dialog =
  | { kind: 'details'; soldier: Soldier }
  | { kind: 'create' }
  | { kind: 'edit'; soldier: Soldier }
  | { kind: 'status'; soldier: Soldier }
  /** The one-tap sheet the status badge on a roster row opens. */
  | { kind: 'quick'; soldier: Soldier };

/** The company roster: status tabs, search and the soldier card grid. */
export function SoldiersPage({
  user,
  soldiers,
  vehicles,
  assignments = EMPTY_ASSIGNMENT_INDEX,
  loading,
  reload,
  now = Date.now(),
}: SoldiersPageProps) {
  const [filters, setFilters] = useState<SoldierFilters>(EMPTY_SOLDIER_FILTERS);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  /** The soldier the delete confirmation is open for. */
  const [pendingDelete, setPendingDelete] = useState<Soldier | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** How many crew slots they still hold — read as the confirmation opens. */
  const [usage, setUsage] = useState<number | null>(null);

  const canManage = hasPermission(user, 'MANAGE_SOLDIERS');
  const counts = useMemo(
    () => soldierAvailabilityCounts(soldiers, now, assignments),
    [soldiers, now, assignments],
  );
  const visible = useMemo(
    () => filterSoldiers(soldiers, filters, now, assignments),
    [soldiers, filters, now, assignments],
  );

  // The crew count is read once per confirmation, so the warning is on the
  // screen before the delete is attempted rather than after it is refused.
  useEffect(() => {
    if (!pendingDelete) {
      setUsage(null);
      return;
    }
    let live = true;
    void countSoldierUsage(pendingDelete.id).then((count) => {
      if (live) setUsage(count);
    });
    return () => {
      live = false;
    };
  }, [pendingDelete]);

  const closeConfirm = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteSoldier(user, pendingDelete.id);
      // A soldier whose card was open is no longer on the roster.
      if (dialog && 'soldier' in dialog && dialog.soldier.id === pendingDelete.id) setDialog(null);
      setPendingDelete(null);
      await reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Users size={20} className="text-brand-500" aria-hidden />
          חיילים
        </h2>
        {canManage ? (
          <button
            type="button"
            onClick={() => setDialog({ kind: 'create' })}
            className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-2"
          >
            <Plus size={16} aria-hidden />
            חייל חדש
          </button>
        ) : null}
      </div>

      <nav aria-label="סינון לפי סטטוס" className="flex gap-1.5 overflow-x-auto pb-1">
        {AVAILABILITY_TABS.map((availability) => {
          const isActive = filters.availability === availability;
          const label = availability === ANY ? 'הכול' : SOLDIER_AVAILABILITY_LABELS[availability];
          return (
            <button
              key={availability}
              type="button"
              onClick={() => setFilters({ ...filters, availability })}
              aria-current={isActive ? 'true' : undefined}
              className={[
                'shrink-0 h-9 px-3 rounded-xl border text-sm transition-colors',
                isActive
                  ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold'
                  : 'border-line bg-panel text-ink/60 hover:text-ink',
              ].join(' ')}
            >
              {label}
              <span className="mr-1.5 text-xs text-ink/40 tabular-nums">{counts[availability]}</span>
            </button>
          );
        })}
      </nav>

      <SoldierFilterBar filters={filters} onChange={setFilters} resultCount={visible.length} />

      {loading ? (
        <p className="text-sm text-ink/50 py-8 text-center">טוען את מצבת החיילים…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-ink/50 py-8 text-center">
          לא נמצאו חיילים התואמים את החיפוש{filters.text ? ` "${filters.text}"` : ''}.
        </p>
      ) : (
        // The rows are short enough that two or three columns fit a platoon on
        // one screen; every detail and every action is one tap away in the card.
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((soldier) => (
            <SoldierCard
              key={soldier.id}
              soldier={soldier}
              assignments={assignments}
              now={now}
              onOpen={(picked) => setDialog({ kind: 'details', soldier: picked })}
              onQuickStatus={
                canManage ? (picked) => setDialog({ kind: 'quick', soldier: picked }) : undefined
              }
            />
          ))}
        </div>
      )}

      {dialog?.kind === 'details' ? (
        <SoldierDetailsModal
          soldier={dialog.soldier}
          vehicles={vehicles}
          user={user}
          now={now}
          onClose={() => setDialog(null)}
          onEdit={(soldier) => setDialog({ kind: 'edit', soldier })}
          onChangeStatus={(soldier) => setDialog({ kind: 'status', soldier })}
          onDelete={canManage ? (soldier) => setPendingDelete(soldier) : undefined}
        />
      ) : null}

      {dialog?.kind === 'create' || dialog?.kind === 'edit' ? (
        <SoldierFormModal
          user={user}
          soldier={dialog.kind === 'edit' ? dialog.soldier : null}
          onClose={() => setDialog(null)}
          onSaved={reload}
        />
      ) : null}

      {dialog?.kind === 'quick' ? (
        <SoldierQuickStatusSheet
          user={user}
          soldier={dialog.soldier}
          now={now}
          onClose={() => setDialog(null)}
          onSaved={reload}
          onAdvanced={(soldier) => setDialog({ kind: 'status', soldier })}
        />
      ) : null}

      {dialog?.kind === 'status' ? (
        <SoldierStatusModal
          user={user}
          soldier={dialog.soldier}
          onClose={() => setDialog(null)}
          onSaved={reload}
        />
      ) : null}

      {pendingDelete ? (
        <Modal
          title="מחיקת חייל"
          subtitle={`${pendingDelete.fullName} · ${pendingDelete.personalNumber}`}
          onClose={closeConfirm}
          footer={
            <>
              <button
                type="button"
                onClick={closeConfirm}
                className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={deleting || usage !== 0}
                onClick={() => void handleDelete()}
                className="h-10 px-5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                {deleting ? 'מוחק…' : 'מחק'}
              </button>
            </>
          }
        >
          <p className="text-sm">האם אתה בטוח שברצונך למחוק חייל זה?</p>
          <p className="mt-2 text-xs text-ink/50">
            החייל יוסר לצמיתות ממצבת החיילים, יחד עם כל החרגה שנרשמה עליו. לא ניתן לשחזר פעולה זו.
          </p>
          {usage === null ? (
            <p className="mt-3 text-xs text-ink/50">בודק שיבוצים קיימים…</p>
          ) : usage > 0 ? (
            <p className="mt-3 text-sm rounded-xl border border-amber-200 bg-amber-50 text-amber-800 p-3">
              החייל משובץ ל־{usage} משימות. יש להסיר אותו מהשיבוצים לפני המחיקה.
            </p>
          ) : null}
          {deleteError ? (
            <p className="mt-3 text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
              {deleteError}
            </p>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
